import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import emailService from "@services/email.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { THREAD_AI_CONTEXT_QUERY_KEY, QUERY_CACHE_SHORT_EXPIRATION_MS } from "@config/query";

export function useAIContext(threadId: string) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const query = useQuery({
    enabled: Boolean(threadId && threadId.length),
    queryKey: [THREAD_AI_CONTEXT_QUERY_KEY, threadId],
    queryFn: async () => {
      return await emailService.getThreadAIContext(threadId);
    },
    staleTime: QUERY_CACHE_SHORT_EXPIRATION_MS,
    retry: false,
    placeholderData: keepPreviousData,
  });

  const regenerateContext = async () => {
    try {
      const params = new URLSearchParams({ regenerate: "true" });
      const response = await emailService.getThreadAIContext(threadId, params);
      queryClient.setQueryData([THREAD_AI_CONTEXT_QUERY_KEY, threadId], response);
      return response;
    } catch (error) {
      if (error instanceof DisconnectedError) return null;
      notifyBugsnag(error, ERROR_STRINGS.LOAD_AI_CONTEXT);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.LOAD_AI_CONTEXT);
      return null;
    }
  };

  return {
    ...query,
    refetchContext: query.refetch, // regular refetch
    regenerateContext, // refetch with params
  };
}
